import React, { createContext, useState, useEffect, useContext } from 'react';
import { api } from '../api/client';

const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [uncertainties, setUncertainties] = useState([]);
  const [votes, setVotes] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [presenterMode, setPresenterMode] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // On mount: restore user from localStorage and verify token is still valid
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('current_user');

      if (token && storedUser) {
        try {
          // Make a lightweight authenticated request to verify the token
          await api.getSessions();
          setCurrentUser(JSON.parse(storedUser));
        } catch (error) {
          // Token is expired or invalid -- clear everything
          localStorage.removeItem('access_token');
          localStorage.removeItem('current_user');
          // session-expired flag is set by the event listener below
        }
      }
      setIsInitialized(true);
    };

    initializeAuth();
  }, []);

  // Listen for session-expired events fired by authFetch on any 401
  useEffect(() => {
    const handleSessionExpired = () => {
      setCurrentUser(null);
      setSessions([]);
      setSessionExpired(true);
    };

    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, []);

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('current_user', JSON.stringify(currentUser));
      setSessionExpired(false);
    } else {
      localStorage.removeItem('current_user');
    }
  }, [currentUser]);

  // Load sessions when user is set
  useEffect(() => {
    if (currentUser) {
      loadSessions();
    }
  }, [currentUser]);

  const loadSessions = async () => {
    try {
      const data = await api.getSessions();
      setSessions(data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const value = {
    currentUser,
    setCurrentUser,
    sessions,
    setSessions,
    activeSession,
    setActiveSession,
    uncertainties,
    setUncertainties,
    votes,
    setVotes,
    uploadedFiles,
    setUploadedFiles,
    loadSessions,
    isInitialized,
    presenterMode,
    setPresenterMode,
    sessionExpired,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContext;
